import replayMiddleware from '@redux/middlewares/replay.middleware';
import { rtkQueryErrorLogger } from '@redux/middlewares/rtkQueryErrorHandler.middleware';
import { configureStore, Middleware, UnknownAction } from '@reduxjs/toolkit';
import { setupListeners } from '@reduxjs/toolkit/query';
import { authentApi } from '@services/authent/authent.api';
import { loginApi } from '@services/authent/login.api';
import { c2Api } from '@services/c2/c2.api';
import { sseApi } from '@services/c2/sse.api';
import { chatApi } from '@services/chat/chat.api';
import { configApi } from '@services/config/config.api';
import { dataApi } from '@services/data/data.api';
import { replayApi } from '@services/replay/replay.api';
import { utmApi } from '@services/utm/utm.api';
import { persistStore } from 'redux-persist';
import { FLUSH, PAUSE, PERSIST, PURGE, REGISTER, REHYDRATE } from 'redux-persist/es/constants';
import { Config, createStateSyncMiddleware, initMessageListener } from 'redux-state-sync';

import { chatListenerMiddleware } from './middlewares/chat.middleware';
import { dataMiddleware } from './middlewares/data.middleware';
import { filterState } from './middlewares/interceptInitState.middleware';
import { logbookListenerMiddleware } from './middlewares/logbook.middleware';
import rootReducer from './rootReducer';

//List of action that won't be synchronized
const blacklist = [
  FLUSH,
  REHYDRATE,
  PAUSE,
  PERSIST,
  PURGE,
  REGISTER,
  '@@router/CALL_HISTORY_METHOD',
  '@@router/LOCATION_CHANGE',
  'global/updateLogs',
  'global/addLog',
  'global/deleteLog',
  'global/updateLog',
];

const AUTHORIZED_MAP_ACTIONS = [
  'maps/updateSelectedFeatureKeyByMapId',
  'maps/deselectFeatureByMapId',
  'maps/updatePopupByMapIdFromCockpit',
  'maps/updateStatisticsCriteria',
];

interface MetaArg {
  endpointName?: string;
}

interface Meta {
  arg?: MetaArg;
}

function isMetaWithArg(meta: unknown): meta is Meta {
  return !!meta && typeof meta === 'object' && 'arg' in meta && typeof meta.arg === 'object';
}

function containEndpointName(action: UnknownAction, endpoints: string[]): boolean {
  if (isMetaWithArg(action.meta)) {
    const endpointName = action.meta.arg?.endpointName;
    return typeof endpointName === 'string' && endpoints.includes(endpointName);
  }
  return false;
}

const config: Config = {
  channel: 'general',
  //Prevent blacklisted action synchronization between browser tab
  //Also blacklist maps actions beside a few authorized
  predicate: (action: UnknownAction) => {
    return (
      !blacklist.includes(action.type) &&
      !action.type.includes('/Rejected') &&
      !action.type.includes('situation/') &&
      !action.type.startsWith('utm/') &&
      (action.type.startsWith('mapsCommands/') ||
        !action.type.includes('maps/') ||
        AUTHORIZED_MAP_ACTIONS.includes(action.type)) &&
      !containEndpointName(action, [
        'getMonitoringStream',
        'getSituation',
        'getModelsStream',
        'getSituationDiff',
        'getReplayStream',
        'updateStream',
      ])
    );
  },
  prepareState: filterState,
  broadcastChannelOption: { type: 'localstorage' },
};

const middlewares: Middleware[] = [
  createStateSyncMiddleware(config) as Middleware,
  authentApi.middleware,
  loginApi.middleware,
  c2Api.middleware,
  sseApi.middleware,
  utmApi.middleware,
  configApi.middleware,
  replayApi.middleware,
  dataApi.middleware,
  chatApi.middleware,
  replayMiddleware.middleware,
  rtkQueryErrorLogger,
];

// const logger = createLogger({
//   collapsed: (getState, action, logEntry) => !logEntry?.error,
// });

// if (process.env.NODE_ENV === 'development') {
//   middlewares.push(logger);
// }

export const store = configureStore({
  devTools: process.env.NODE_ENV === 'development',
  middleware: (getDefaultMiddleware) =>
    getDefaultMiddleware({
      //Our store is too big for those middlewares to run in a short time
      //which leads to a lot of warning during development so we disable them
      serializableCheck: false, // ignoredActions: [FLUSH, REHYDRATE, PAUSE, PERSIST, PURGE, REGISTER],
      immutableCheck: false,
    })
      .concat(middlewares)
      .prepend([chatListenerMiddleware.middleware, logbookListenerMiddleware.middleware, dataMiddleware.middleware]),
  reducer: rootReducer,
});

// Infer the `RootState` and `AppDispatch` types from the store itself
export type StoreType = typeof store;
export type RootState = ReturnType<typeof store.getState>;
export type AppDispatch = typeof store.dispatch;

setupListeners(store.dispatch);

initMessageListener(store);

export const persistor = persistStore(store);
